import { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { defaultTo, isNil, isString, noop } from 'lodash/fp';
import { useFormContext } from './Form.jsx';

function hasTrimTest(schema) {
    try {
        return Boolean(schema?.describe().tests?.some((test) => test.name === 'trim'));
    } catch {
        return false;
    }
}

function applySchemaPropFromTest(props, schemaType, test) {
    switch (test?.name) {
        case 'min':
            switch (schemaType) {
                case 'number':
                    props.min = test.params.min;
                    break;
                case 'string':
                    props.minLength = test.params.min;
                    break;
                default:
            }
            break;
        case 'max':
            switch (schemaType) {
                case 'number':
                    props.max = test.params.max;
                    break;
                case 'string':
                    props.maxLength = test.params.max;
                    break;
                default:
            }
            break;
        default:
    }
}

export function Field({
    name,
    children,
    component: Component = 'input',
    onChange = noop,
    onBlur = noop,
    disabled = false,
    error = null,
    ...others
}) {
    const context = useFormContext();

    if (isNil(context)) {
        throw new Error('<Field> must be used within a <Form> component');
    }

    const {
        isSubmitting,
        isFormDisabled,
        getFieldValue,
        getFieldError,
        getFieldSchema,
        setFieldValue,
        setFieldTouched,
        setFieldLabel,
        setFieldVisible,
        resetField
    } = context;

    useEffect(() => {
        setFieldVisible(name, true);
        return () => {
            setFieldVisible(name, false);
        };
    }, [name, setFieldVisible]);

    const { label } = others;

    useEffect(() => {
        if (isNil(setFieldLabel) || !isString(label)) {
            return undefined;
        }

        setFieldLabel(name, label);
        return () => {
            setFieldLabel(name, null);
        };
    }, [name, label, setFieldLabel]);

    const valueAttribute = others.type === 'checkbox' ? 'checked' : 'value';
    const isNativeElement = isString(Component);

    const handleBlur = useCallback(() => {
        let value = getFieldValue(name);

        if (isString(value) && hasTrimTest(getFieldSchema(name))) {
            const trimmed = value.trim();
            if (trimmed !== value) {
                setFieldValue(name, trimmed);
                value = trimmed;
            }
        }

        setFieldTouched(name);
        onBlur(value);
    }, [name, setFieldTouched, getFieldValue, getFieldSchema, setFieldValue, onBlur]);

    const handleChange = useCallback(
        (value) => {
            const previousValue = getFieldValue(name);
            setFieldValue(name, value);
            setFieldTouched(name);
            onChange(value, { previousValue, getFieldValue, setFieldValue, resetField });
        },
        [name, setFieldValue, setFieldTouched, getFieldValue, resetField, onChange]
    );

    const handleNativeChange = useCallback(
        (event) => {
            handleChange(event.target[valueAttribute]);
        },
        [handleChange, valueAttribute]
    );

    const props = {
        name,
        [valueAttribute]: defaultTo(isNativeElement ? '' : undefined, getFieldValue(name)),
        onBlur: handleBlur,
        onChange: isNativeElement ? handleNativeChange : handleChange,
        disabled: isSubmitting || disabled || isFormDisabled,
        error: error ?? getFieldError(name),
        ...others
    };

    const schema = getFieldSchema(name)?.describe();
    if (schema) {
        props.required = !schema.optional;

        if (schema.type === 'number') {
            props.type = 'number';
        }
        for (const test of schema.tests) {
            applySchemaPropFromTest(props, schema.type, test);
        }
    }

    return <Component {...props}>{children}</Component>;
}

Field.propTypes = {
    /**
     * Name of the field used to lookup value in form state.
     */
    name: PropTypes.string.isRequired,

    /**
     * Children passed directly to the underlying component.
     */
    children: PropTypes.node,

    /**
     * Either a React component or the name of an HTML element to render.
     */
    component: PropTypes.oneOfType([
        PropTypes.oneOf(['input', 'select', 'textarea']),
        PropTypes.elementType
    ]),

    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    disabled: PropTypes.bool,

    /**
     * Overwrites error message
     */
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(PropTypes.string)])
};
